import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  isPriceHidden,
  isProductNegotiable,
  resolveCurrencySymbol,
} from "../../utility/utilityFunctions";
import {
  FULL_PRICE_OFFER_NOTE,
  OFFER_PAYMENT_HINT,
  centsToInput,
  formatMoneyInput,
  formatCents,
  isFullPriceOffer,
  offerCeilingHint,
  parseMoneyToCents,
  validateOfferCents,
} from "../../utility/negotiation";
import DeliveryInstructionsBox from "../DeliveryInstructionsBox";

const NOTE_MAX_LENGTH = 300;

// Details of a cart line: an optional per-unit offer (negotiable products
// only) and an optional note for the seller. Mirrors the app's
// AddToCartModal. Prefilled from the product's existing cart line, so adding
// more of an item never silently looks like it dropped the offer or note.
// The offer becomes binding only when the order is placed (in the app).
const AddToCartModal = ({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  orderInstructions,
  deliveryInstructions,
  product,
  quantity = 1,
  initialNote,
  initialOfferPriceCents,
  startWithOffer = false,
  submitError,
  // 'edit': changing an existing cart line's offer/note from the cart.
  mode = "add",
}) => {
  const negotiable = isProductNegotiable(product);
  // No price to buy at, so the offer is the whole point.
  const offerRequired = isPriceHidden(product);
  const [note, setNote] = useState("");
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerText, setOfferText] = useState("");
  const [offerError, setOfferError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setNote(initialNote || "");
    setOfferText(centsToInput(initialOfferPriceCents));
    setOfferOpen(
      offerRequired ||
        (negotiable && (startWithOffer || initialOfferPriceCents != null)),
    );
    setOfferError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const offerCents = offerOpen ? parseMoneyToCents(offerText) : null;
  const isFullPrice = offerOpen && isFullPriceOffer(product, offerCents);
  const hasOffer = offerOpen && offerCents != null && !isFullPrice;
  const qty = Math.max(1, parseInt(quantity, 10) || 1);
  const currency = product?.currency;

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleSubmit = () => {
    if (offerRequired && !offerText.trim()) {
      setOfferError("Enter your offer.");
      return;
    }
    if (offerOpen && offerText.trim()) {
      const error = validateOfferCents(product, offerCents);
      if (error) {
        setOfferError(error);
        return;
      }
    }
    onSubmit({
      note: note.trim(),
      // null clears an offer the cart line already had; undefined leaves a
      // non-negotiable line untouched.
      offerPriceCents: negotiable ? (hasOffer ? offerCents : null) : undefined,
    });
  };

  const removeOffer = () => {
    setOfferOpen(false);
    setOfferText("");
    setOfferError(null);
  };

  const title =
    mode === "edit"
      ? "Edit offer and note"
      : startWithOffer || offerRequired
        ? "Make an offer"
        : "Add to cart";
  const submitLabel =
    mode === "edit"
      ? "Save"
      : offerRequired
        ? "Add offer to cart"
        : hasOffer
          ? "Add with offer"
          : "Add to Cart";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography fontWeight={700} fontSize={18}>
          {title}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {orderInstructions && (
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              bgcolor: "#f0f9ff",
              border: "1px solid #bae6fd",
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: "#0369a1", mb: 0.5 }}
            >
              Seller's instructions
            </Typography>
            <Typography variant="body2" sx={{ color: "#0369a1" }}>
              {orderInstructions}
            </Typography>
          </Box>
        )}
        <DeliveryInstructionsBox
          instructions={deliveryInstructions}
          sx={{ mb: 2 }}
        />

        {negotiable &&
          (offerOpen ? (
            <Box sx={{ mb: 2.5 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography fontWeight={600} fontSize={14}>
                  Your offer (per item)
                </Typography>
                {offerRequired ? null : (
                  <Button
                    size="small"
                    onClick={removeOffer}
                    sx={{ textTransform: "none", minWidth: 0, p: 0 }}
                  >
                    Remove offer
                  </Button>
                )}
              </Box>
              <TextField
                fullWidth
                size="small"
                value={offerText}
                onChange={(e) => {
                  setOfferText(formatMoneyInput(e.target.value));
                  setOfferError(null);
                }}
                autoFocus={
                  (startWithOffer || offerRequired) &&
                  initialOfferPriceCents == null
                }
                placeholder="0"
                inputProps={{ inputMode: "decimal" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {resolveCurrencySymbol(currency)}
                    </InputAdornment>
                  ),
                }}
                error={!!(offerError || submitError)}
                helperText={
                  offerError ||
                  submitError ||
                  (isFullPrice
                    ? FULL_PRICE_OFFER_NOTE
                    : offerCeilingHint(product))
                }
              />
              {hasOffer && qty > 1 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mt={0.5}
                >
                  {qty} × {formatCents(offerCents, currency)} ={" "}
                  {formatCents(offerCents * qty, currency)}
                </Typography>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={1}
              >
                {OFFER_PAYMENT_HINT} You'll complete your order in the Ngwaahia
                app.
              </Typography>
            </Box>
          ) : (
            <Box
              role="button"
              tabIndex={0}
              onClick={() => setOfferOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setOfferOpen(true);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px solid #e5e7eb",
                borderRadius: 2,
                px: 1.5,
                py: 1.25,
                mb: 2.5,
                cursor: "pointer",
                "&:hover": { bgcolor: "#f9fafb" },
              }}
            >
              <Box>
                <Typography fontWeight={600} fontSize={14}>
                  Make an offer (optional)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {offerCeilingHint(product)}
                </Typography>
              </Box>
              <ChevronRightIcon sx={{ color: "#6b7280" }} />
            </Box>
          ))}

        <Typography color="text.secondary" sx={{ mb: 2 }} fontSize={14}>
          Want to specify a size, color, or other preference for this item? Add
          a note below (optional).
        </Typography>

        <TextField
          fullWidth
          multiline
          minRows={3}
          placeholder="e.g. size, color, or other preferences"
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX_LENGTH))}
          sx={{ mb: 0.5 }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: "right", mb: 2 }}
        >
          {note.length}/{NOTE_MAX_LENGTH}
        </Typography>

        {submitError && !offerOpen && (
          <Typography variant="body2" color="error.main" sx={{ mb: 1.5 }}>
            {submitError}
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{
              bgcolor: "#046287",
              "&:hover": { bgcolor: "#035070" },
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            {isSubmitting ? "Saving…" : submitLabel}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddToCartModal;
